import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cx } from '../lib/cx';

import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  /** Shows a spinner, disables the button and announces the busy state. */
  loading?: boolean;
  /** Copy shown while `loading`; falls back to the normal children. */
  loadingLabel?: string;
  size?: 'sm' | 'md';
  /** Defaults to `button`, never an accidental form submit. */
  type?: 'button' | 'submit' | 'reset';
  children: ReactNode;
}

/**
 * The one button primitive. Real `<button>` (a11y §8.6.1), variant-driven, with
 * a first-class loading state that keeps the control in the tab order but
 * disabled and `aria-busy`.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading = false, loadingLabel, size = 'md', type = 'button', disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('ds-btn', `ds-btn--${variant}`, size === 'sm' && 'ds-btn--sm', className)}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : null}
      {loading && loadingLabel !== undefined ? loadingLabel : children}
    </button>
  );
});
